import { useEffect, useRef } from 'react'

import { useMutation } from '@tanstack/react-query'
import { WithReactQueryOptions } from 'ComponentLibrary'
import {
    type AiUseCase,
    type AiWorkflowRequest,
    type AiWorkflowResponse,
    type AsyncInvokeAgentPartialResult,
    type AsyncInvokeAgentProgressSnapshot,
} from '@einwert/valuation-api-client'

import { useGlobalMutationErrorHandler } from 'ClientPortal/src/api/useGlobalMutationErrorHandler'
import { getValuationApiClient } from 'ClientPortal/src/api/valuationApiClient'

export function useInvokeWorkflow({
    agentUseCase,
    onProgress,
    onPartialResult,
    onSuccess,
    onError,
}: WithReactQueryOptions<
    {
        agentUseCase: AiUseCase
        onProgress?: (progress: AsyncInvokeAgentProgressSnapshot) => void
        onPartialResult?: (partialResult: AsyncInvokeAgentPartialResult) => void
    },
    AiWorkflowResponse
>) {
    const pollingControllersRef = usePollingControllers()
    const handleError = useGlobalMutationErrorHandler(onError)
    const invokeWorkflow = async (request: AiWorkflowRequest): Promise<AiWorkflowResponse> => {
        const client = getValuationApiClient().ai
        const pollingController = new AbortController()
        pollingControllersRef.current.add(pollingController)
        try {
            return await client.invokeWorkflowWithAsyncPolling({
                agentUseCase,
                request,
                onProgress,
                onPartialResult,
                signal: pollingController.signal,
            })
        } finally {
            pollingControllersRef.current.delete(pollingController)
        }
    }

    const mutation = useMutation<AiWorkflowResponse, unknown, AiWorkflowRequest>({
        mutationFn: invokeWorkflow,
        onSuccess,
        onError: handleError,
    })
    return mutation
}
const usePollingControllers = () => {
    const controllersRef = useRef(new Set<AbortController>())

    useEffect(
        () => () => {
            for (const controller of controllersRef.current) {
                controller.abort()
            }
            controllersRef.current.clear()
        },
        []
    )

    return controllersRef
}
